import React, { useState, useEffect } from 'react';

interface LocationPickerProps {
    initialLat?: number;
    initialLng?: number;
    onLocationChange: (lat: number, lng: number) => void;
}

const LocationPicker: React.FC<LocationPickerProps> = ({ initialLat, initialLng, onLocationChange }) => {
    const [lat, setLat] = useState<string>(initialLat ? String(initialLat) : '');
    const [lng, setLng] = useState<string>(initialLng ? String(initialLng) : '');
    const [isFetching, setIsFetching] = useState(false);
    const [locationError, setLocationError] = useState<string | null>(null);
    const [successMsg, setSuccessMsg] = useState<string | null>(null);

    useEffect(() => {
        if (initialLat) setLat(String(initialLat));
        if (initialLng) setLng(String(initialLng));
    }, [initialLat, initialLng]);

    const handleUseMyLocation = () => {
        if (!navigator.geolocation) {
            setLocationError('Geolocation is not supported by your browser.');
            return;
        }
        setIsFetching(true);
        setLocationError(null);
        setSuccessMsg(null);
        navigator.geolocation.getCurrentPosition(
            (pos) => {
                const newLat = parseFloat(pos.coords.latitude.toFixed(6));
                const newLng = parseFloat(pos.coords.longitude.toFixed(6));
                setLat(String(newLat));
                setLng(String(newLng));
                onLocationChange(newLat, newLng);
                setSuccessMsg('Location fetched successfully!');
                setIsFetching(false);
            },
            (err) => {
                setLocationError(
                    err.code === err.PERMISSION_DENIED
                        ? 'Location permission denied. Please enter manually.'
                        : 'Unable to fetch location. Please enter manually.'
                );
                setIsFetching(false);
            },
            { enableHighAccuracy: true }
        );
    };

    const handleLatChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        setLat(e.target.value);
        setSuccessMsg(null);
        const parsed = parseFloat(e.target.value);
        if (!isNaN(parsed) && lng) {
            onLocationChange(parsed, parseFloat(lng));
        }
    };

    const handleLngChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        setLng(e.target.value);
        setSuccessMsg(null);
        const parsed = parseFloat(e.target.value);
        if (!isNaN(parsed) && lat) {
            onLocationChange(parseFloat(lat), parsed);
        }
    };

    return (
        <div style={{ width: '100%' }}>
            {/* Use My Location Button */}
            <button
                type="button"
                onClick={handleUseMyLocation}
                disabled={isFetching}
                style={{
                    width: '100%',
                    padding: '12px 16px',
                    backgroundColor: isFetching ? '#888' : '#276859',
                    color: '#fff',
                    border: 'none',
                    borderRadius: 8,
                    fontWeight: 700,
                    fontSize: 14,
                    cursor: isFetching ? 'not-allowed' : 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: 8,
                    marginBottom: 10,
                }}
            >
                {isFetching ? (
                    <>
                        <span style={{ width: 16, height: 16, border: '2px solid #fff', borderTopColor: 'transparent', borderRadius: '50%', display: 'inline-block', animation: 'spin 0.7s linear infinite' }} />
                        Fetching your location...
                    </>
                ) : (
                    <>📡 Use My Current Location</>
                )}
            </button>

            {locationError && (
                <div style={{ color: '#c0392b', fontSize: 13, marginBottom: 10, padding: '8px 12px', background: '#fdecea', borderRadius: 6 }}>
                    ⚠️ {locationError}
                </div>
            )}
            {successMsg && (
                <div style={{ color: '#276859', fontSize: 13, marginBottom: 10, padding: '8px 12px', background: '#e8f5e9', borderRadius: 6 }}>
                    ✅ {successMsg}
                </div>
            )}

            {/* Divider */}
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 10 }}>
                <div style={{ flex: 1, height: 1, background: '#ddd' }} />
                <span style={{ fontSize: 12, color: '#888' }}>or enter manually</span>
                <div style={{ flex: 1, height: 1, background: '#ddd' }} />
            </div>

            {/* Manual lat/lng fields */}
            <div style={{ display: 'flex', gap: 12 }}>
                <div style={{ flex: 1 }}>
                    <label style={{ fontSize: 12, fontWeight: 600, color: '#444', display: 'block', marginBottom: 4 }}>
                        Latitude
                    </label>
                    <input
                        type="number"
                        value={lat}
                        onChange={handleLatChange}
                        placeholder="e.g. 12.971599"
                        step="any"
                        style={{
                            width: '100%',
                            padding: '9px 12px',
                            border: '1px solid #ccc',
                            borderRadius: 6,
                            fontSize: 14,
                            outline: 'none',
                            boxSizing: 'border-box',
                        }}
                    />
                </div>
                <div style={{ flex: 1 }}>
                    <label style={{ fontSize: 12, fontWeight: 600, color: '#444', display: 'block', marginBottom: 4 }}>
                        Longitude
                    </label>
                    <input
                        type="number"
                        value={lng}
                        onChange={handleLngChange}
                        placeholder="e.g. 77.594563"
                        step="any"
                        style={{
                            width: '100%',
                            padding: '9px 12px',
                            border: '1px solid #ccc',
                            borderRadius: 6,
                            fontSize: 14,
                            outline: 'none',
                            boxSizing: 'border-box',
                        }}
                    />
                </div>
            </div>

            <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
        </div>
    );
};

export default LocationPicker;
